// Shared data for the Passing widgets (Summary + Radar): the player on a
// POSITIONAL basis (per-90s over the minutes in their position group) plus the
// peer distribution's average for that group — the "compared to N Strikers"
// story. Both widgets read the same thing, so it lives in one hook.
//
// Positional basis is what makes our numbers match old Catalyst exactly
// (verified: Messi MLS 2025 striker-filtered → 2403.78 min / 1193 passes /
// 0.23 diff / PSAX-90 1.06, and 135 peers with mean difficulty 0.22).
//
// Until the comparison drawer lands, the group/basis/floor are FIXED:
//   group = the player's top position's template · positional minutes · 150 floor.

import { useMemo } from "react";
import {
  usePlayerPositionMinutes,
  usePlayerStats,
  usePlayerStatsPeers,
  usePlayerStatsGroupAvg,
} from "../data/players.js";
import { pickTemplateByPosition } from "./radar.js";
import { RADAR_TEMPLATES } from "./radarTemplates.js";
import { flooredPeers, type StatsRow } from "./passing.js";

export const PASSING_MINUTES_FLOOR = 150;

export interface PassingComparison {
  /** Position-group label ("Striker", …), or null if the player matches none. */
  groupLabel: string | null;
  /** Whether a peer group was resolved (drives the comparison overlay/sub-labels). */
  hasGroup: boolean;
  /** The player's positional (position-filtered) per-90 row. */
  playerRow: StatsRow | null;
  /** The group's LEAGUE SEASON AVERAGE row (volume-weighted) — old Catalyst's
   *  default comparison basis, used for the overlay + "positional avg" labels.
   *  The drawer (later) will let this switch to the unweighted mean / median. */
  peerAvg: StatsRow | null;
  /** Every peer that cleared the minutes floor (incl. the player) — the
   *  distribution the percentile bars rank against. */
  peerRows: StatsRow[];
  /** How many peers cleared the minutes floor (the "vs N Strikers" count). */
  peerCount: number;
  isPending: boolean;
  isError: boolean;
  error: unknown;
}

/**
 * The one-line scope note shown atop every Passing widget, so each tile states
 * its own comparison basis (old Catalyst put it once at the top of the whole
 * tab; our widgets are independent). Mirrors old Catalyst's wording.
 */
export function passingScopeText(cmp: PassingComparison): string {
  return cmp.hasGroup
    ? `Positional minutes · vs ${cmp.peerCount} ${cmp.groupLabel}s (${PASSING_MINUTES_FLOOR}+ min)`
    : "Overall minutes · no position group for peer comparison";
}

export function usePassingComparison(
  playerId: number | null,
  competitionId: number | null,
  seasonId: number | null,
  keys: string[],
  minutesFloor: number = PASSING_MINUTES_FLOOR,
): PassingComparison {
  const posMins = usePlayerPositionMinutes(playerId, competitionId, seasonId);

  // The peer group = the position template covering the player's most-played
  // position (same grouping the Radar widget auto-picks). null when none match.
  const group = posMins.data ? pickTemplateByPosition(RADAR_TEMPLATES, posMins.data.rows) : null;
  const peerPositions = group?.positions ?? [];

  // Hold the player query until we know the group, so we don't fire an overall
  // (unfiltered) fetch and then immediately refire it position-filtered.
  const playerKeys = posMins.isPending ? [] : keys;
  const player = usePlayerStats(playerId, competitionId, seasonId, playerKeys, peerPositions);
  // Distribution (per player) → the "vs N" count now, and the percentile bars later.
  const peers = usePlayerStatsPeers(competitionId, seasonId, keys, peerPositions);
  // League season average (one volume-weighted row) → the comparison basis.
  const leagueAvg = usePlayerStatsGroupAvg(competitionId, seasonId, keys, peerPositions);

  const peerRows = useMemo(
    () => (peers.data ? flooredPeers(peers.data.rows, minutesFloor) : []),
    [peers.data, minutesFloor],
  );

  const hasGroup = peerPositions.length > 0;
  return {
    groupLabel: group?.label ?? null,
    hasGroup,
    playerRow: player.data?.rows[0] ?? null,
    peerAvg: leagueAvg.data?.rows[0] ?? null,
    peerRows,
    peerCount: peerRows.length,
    isPending:
      posMins.isPending ||
      player.isPending ||
      (hasGroup && (peers.isPending || leagueAvg.isPending)),
    isError: posMins.isError || player.isError || peers.isError || leagueAvg.isError,
    error: posMins.error ?? player.error ?? peers.error ?? leagueAvg.error,
  };
}
